import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastService, ToastOptions } from '../services/toast/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { getSafeErrorMessage } from '../utils/error-message.util';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        const serviceResult: any = error.error;
        const failDetails = serviceResult?.fail;
        const statusCode = error?.status;

        if ((failDetails && failDetails.status === 401) || statusCode === 401) {
          return throwError(() => error);
        }

        let toastOptions: ToastOptions;

        if (
          failDetails &&
          failDetails.status === 400 &&
          (failDetails.title === 'Validation errors occurred' ||
            failDetails.title === 'Validation errors occured')
        ) {
          if (environment.isUsbsEnvironment) {
            toastOptions = {
              header: this.translateService.instant('toasts.validation.header'),
              body: this.translateService.instant('errors.usbs.generic'),
              classname: 'bg-danger text-white',
              autohide: true,
              delay: 5000,
            };
          } else {
            const genericMessage = this.translateService.instant(
              'validation.bsnNumber.tcKimlikInvalid'
            );
            let errorListHtml = '<ul>';
            for (const key in failDetails) {
              if (Array.isArray(failDetails[key])) {
                failDetails[key].forEach((err: string) => {
                  errorListHtml += `<li>${getSafeErrorMessage(
                    err,
                    this.translateService,
                    'validation.bsnNumber.tcKimlikInvalid'
                  )}</li>`;
                });
              }
            }

            errorListHtml += '</ul>';

            toastOptions = {
              header: this.translateService.instant('toasts.validation.header'),
              body: errorListHtml,
              classname: 'bg-warning text-dark',
              isHtml: true,
              autohide: true,
              delay: 5000,
            };
          }
          this.toastService.show(toastOptions);
          return throwError(() => error);
        }

        if (serviceResult && serviceResult.status === 403) {
          toastOptions = {
            header: this.translateService.instant('toasts.forbidden.title'),
            body: this.translateService.instant('toasts.forbidden.message'),
            classname: 'bg-danger text-white',
            autohide: true,
            delay: 5000,
          };
          this.toastService.show(toastOptions);
          return throwError(() => error);
        }

        if (statusCode === 413 || failDetails?.status === 413) {
          toastOptions = {
            header: this.translateService.instant(
              'toasts.payload_too_large.title'
            ),
            body: this.translateService.instant(
              'toasts.payload_too_large.message'
            ),
            classname: 'bg-warning text-dark',
            autohide: true,
            delay: 5000,
          };
          this.toastService.show(toastOptions);
          return throwError(() => error);
        }

        if (
          failDetails &&
          failDetails.status >= 500 &&
          failDetails.status < 600
        ) {
          toastOptions = {
            header: this.translateService.instant('toasts.server_error.title'),
            body: this.translateService.instant('toasts.server_error.message'),
            classname: 'bg-danger text-white',
            autohide: true,
            delay: 5000,
          };
          this.toastService.show(toastOptions);
          return throwError(() => error);
        }

        const defaultTitle =
          this.translateService.instant('toasts.error.title');
        const defaultMessage = this.translateService.instant(
          'toasts.error.message'
        );
        const errorMessage = getSafeErrorMessage(
          failDetails?.title || defaultMessage,
          this.translateService,
          'validation.bsnNumber.tcKimlikInvalid'
        );

        toastOptions = {
          header: defaultTitle,
          body: errorMessage || defaultMessage,
          classname: 'bg-danger text-white',
          autohide: true,
          delay: 5000,
        };
        this.toastService.show(toastOptions);

        return throwError(() => error);
      })
    );
  }
}
